import React, { Component, ReactNode } from 'react';

import { Button } from '@splitfiesta/ui';

import { useRouter } from '@/hooks/useRouteChangeRouter';

import {
  ErrorFactory,
  addBreadcrumb,
  isAuthenticationError,
  isAuthorizationError,
  logError,
} from '@/lib/errors';

import { useAuthStore } from '@/stores/authStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

interface RouterProp {
  router: {
    push: (path: string) => void;
  };
}

class AuthErrorBoundaryClass extends Component<Props & RouterProp, State> {
  constructor(props: Props & RouterProp) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's an authentication/authorization error
    const isAuthError =
      isAuthenticationError(error) ||
      isAuthorizationError(error) ||
      error.message === 'Authentication required' ||
      error.message?.includes('401') ||
      error.message?.includes('Unauthorized');

    if (isAuthError) {
      return { hasError: true, error };
    }
    return { hasError: false };
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo) {
    const isAuthError =
      isAuthenticationError(error) ||
      isAuthorizationError(error) ||
      error.message === 'Authentication required' ||
      error.message?.includes('401') ||
      error.message?.includes('Unauthorized');

    if (isAuthError) {
      // Normalize to proper AuthError if needed
      const authError = isAuthenticationError(error)
        ? error
        : ErrorFactory.createAuthError(error.message, 'authentication', {
            redirectTo: '/login',
          });

      // Log the error
      logError(authError, {
        user: undefined,
        isAuthenticated: false,
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        timestamp: new Date(),
      });

      // Add breadcrumb
      addBreadcrumb({
        category: 'user_action',
        message:
          'Authentication error - clearing auth and redirecting to login',
        level: 'warning',
        data: {
          errorMessage: error.message,
        },
      });

      // Clear auth data using Zustand store
      const { clearAuth } = useAuthStore.getState();
      clearAuth();

      // Redirect to login (user will go to home after successful login)
      this.props.router.push('/login');
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-4">Please log in to continue.</p>
            <Button
              variant="primary"
              onClick={() => (window.location.href = '/login')}
              className="bg-linear-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              Go to Login
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to use the router hook
export function AuthErrorBoundary({ children, fallback }: Props) {
  const router = useRouter();
  return (
    <AuthErrorBoundaryClass router={router} fallback={fallback}>
      {children}
    </AuthErrorBoundaryClass>
  );
}
